/**
 * Pre-publish checks for bestek (zero prices, etc.).
 * Materials (raming and diagnose) and behandelingskost (forfait) must not be 0.
 * A diagnose task may be €0 in total, but not when minutes were booked.
 * A leverancierstaak raming must carry a klantkost.
 */

const { getEstimateLineId, getRepairRamingen } = require("./bestekRamingen");

const ZERO_PRICE_OVERRIDE_PHRASE = "PUBLICEREN";
/** @deprecated use ZERO_PRICE_OVERRIDE_PHRASE */
const ZERO_MATERIAL_OVERRIDE_PHRASE = ZERO_PRICE_OVERRIDE_PHRASE;

const isZeroPriceAmount = (value) => {
  const n = Number(value);
  return !Number.isFinite(n) || n === 0;
};

/** Material line with unitPrice or total at 0 (or non-finite). */
const isZeroPriceMaterialLine = (line = {}) =>
  isZeroPriceAmount(line.unitPrice) || isZeroPriceAmount(line.total);

const isZeroForfaitDiagnosticCost = (bestek = {}) =>
  isZeroPriceAmount(bestek.forfaitDiagnosticCost);

/**
 * All material lines on the bestek that have a zero price.
 * @returns {{ kind: string, ramingIndex: number, materialIndex: number, ramingId: string, ramingDescription: string, artNr: string, description: string, unitPrice: number, total: number, lineId: string }[]}
 */
const getZeroPriceMaterials = (bestek = {}) => {
  const issues = [];
  getRepairRamingen(bestek).forEach((raming, ramingIndex) => {
    const ramingDescription =
      raming.description ||
      `Raming ${raming.lineNr != null ? raming.lineNr : ramingIndex + 1}`;
    (raming.materialLines || []).forEach((line, materialIndex) => {
      if (!isZeroPriceMaterialLine(line)) return;
      issues.push({
        kind: "material",
        ramingIndex,
        materialIndex,
        ramingId: getEstimateLineId(raming),
        ramingDescription,
        artNr: String(line.artNr || "").trim(),
        description: String(line.description || "").trim(),
        unitPrice: Number(line.unitPrice) || 0,
        total: Number(line.total) || 0,
        lineId: String(
          line._id || line.id || `${ramingIndex}-${materialIndex}`
        ),
      });
    });
  });
  return issues;
};

const hasZeroPriceMaterials = (bestek = {}) =>
  getZeroPriceMaterials(bestek).length > 0;

/** RPM lines from older bestekken only carry the internal note marker. */
const isDiagnosisMaterialLine = (line = {}) =>
  line.isMaterial === true ||
  String(line.internalNote || "").startsWith("Diagnosemateriaal");

const isZeroPriceDiagnosisMaterial = (line = {}) =>
  isDiagnosisMaterialLine(line) && isZeroPriceAmount(line.HTTOTEXTRA);

/** FinERP werk/verpl are hours: booked time with a €0 amount is a pricing gap. */
const isUnpricedDiagnosisTask = (line = {}) => {
  if (isDiagnosisMaterialLine(line)) return false;
  const workWithoutPrice =
    Number(line.HTWERK) > 0 && isZeroPriceAmount(line.HTTOTWERK);
  const travelWithoutPrice =
    Number(line.HTVERPL) > 0 && isZeroPriceAmount(line.HTTOTVERPL);
  return workWithoutPrice || travelWithoutPrice;
};

const formatDiagnosisMinutes = (hours) =>
  Math.round((Number(hours) || 0) * 60);

/**
 * Diagnose materials at €0 and diagnose tasks with minutes but no amount.
 * @returns {{ kind: 'diagnosis-material'|'diagnosis-task'|'supplier-task', label: string, detail: string, lineId: string, diagnosisIndex: number }[]}
 */
const getZeroPriceDiagnosisIssues = (bestek = {}) => {
  const issues = [];
  (bestek.diagnosisLines || []).forEach((line, diagnosisIndex) => {
    const lineId = String(
      line._id || line.sourceHTOBJ || `diagnosis-${diagnosisIndex}`
    );
    const description =
      String(line.description || "").trim() || "Zonder omschrijving";
    if (isZeroPriceDiagnosisMaterial(line)) {
      issues.push({
        kind: "diagnosis-material",
        label: `Diagnosemateriaal: ${description}`,
        detail: "€ 0,00 — materiaal mag nooit nul zijn",
        lineId,
        diagnosisIndex,
      });
    } else if (isUnpricedDiagnosisTask(line)) {
      const minutes =
        formatDiagnosisMinutes(line.HTWERK) +
        formatDiagnosisMinutes(line.HTVERPL);
      issues.push({
        kind: "diagnosis-task",
        label: `Diagnosetaak${
          line.technician ? ` (${line.technician})` : ""
        }: ${description}`,
        detail: `${minutes} min gewerkt, maar € 0,00 — met minuten moet er een bedrag zijn`,
        lineId,
        diagnosisIndex,
      });
    }
  });
  return issues;
};

/** Leverancierstaak pseudo-raming without a klantkost in FinERP. */
const getZeroPriceSupplierTaskIssues = (bestek = {}) =>
  getRepairRamingen(bestek)
    .filter((raming) => raming.isSupplierTask && isZeroPriceAmount(raming.total))
    .map((raming) => ({
      kind: "supplier-task",
      label: raming.description || "Leverancierstaak",
      detail: "€ 0,00 — vul de klantkost in op de leverancierstaak",
      lineId: getEstimateLineId(raming),
    }));

const isZeroPriceDiagnosisLine = (line = {}) =>
  isZeroPriceDiagnosisMaterial(line) || isUnpricedDiagnosisTask(line);

/**
 * Combined zero-price issues that block/warn on publish.
 * @returns {{ kind: 'forfait'|'material'|'diagnosis-material'|'diagnosis-task'|'supplier-task', label: string, detail?: string, lineId: string }[]}
 */
const getZeroPricePublishIssues = (bestek = {}) => {
  const issues = [];
  if (isZeroForfaitDiagnosticCost(bestek)) {
    issues.push({
      kind: "forfait",
      label: "Behandelingskost (ramBehandelkost)",
      detail: "€ 0,00 — mag nooit nul zijn",
      lineId: "forfait-diagnostic-cost",
      amount: Number(bestek.forfaitDiagnosticCost) || 0,
    });
  }
  for (const mat of getZeroPriceMaterials(bestek)) {
    const itemLabel =
      [mat.artNr, mat.description].filter(Boolean).join(" — ") ||
      "Materiaal zonder omschrijving";
    issues.push({
      kind: "material",
      label: `${mat.ramingDescription}: ${itemLabel}`,
      detail: `€/eh ${mat.unitPrice} · totaal ${mat.total}`,
      lineId: mat.lineId,
      ...mat,
    });
  }
  issues.push(...getZeroPriceDiagnosisIssues(bestek));
  issues.push(...getZeroPriceSupplierTaskIssues(bestek));
  return issues;
};

const hasZeroPricePublishIssues = (bestek = {}) =>
  getZeroPricePublishIssues(bestek).length > 0;

/**
 * Throw 400 unless zero prices are explicitly acknowledged.
 * @param {object} bestek
 * @param {boolean} acknowledgeZeroPrices
 */
const assertZeroPricesAcknowledged = (
  bestek,
  acknowledgeZeroPrices = false
) => {
  const issues = getZeroPricePublishIssues(bestek);
  if (issues.length === 0) return;
  if (acknowledgeZeroPrices === true) return;

  const preview = issues
    .slice(0, 5)
    .map((z) => z.label)
    .join("; ");
  const more = issues.length > 5 ? ` (+${issues.length - 5} meer)` : "";
  const err = new Error(
    `Bedragen op €0 gevonden (${issues.length}): ${preview}${more}. Corrigeer in FinERP, of bevestig expliciet dat je toch wilt publiceren.`
  );
  err.status = 400;
  err.code = "ZERO_PRICE_ISSUES";
  err.zeroPriceIssues = issues;
  throw err;
};

/** @deprecated use assertZeroPricesAcknowledged */
const assertZeroMaterialsAcknowledged = (
  bestek,
  acknowledgeZeroMaterialPrices = false
) => assertZeroPricesAcknowledged(bestek, acknowledgeZeroMaterialPrices);

module.exports = {
  ZERO_PRICE_OVERRIDE_PHRASE,
  ZERO_MATERIAL_OVERRIDE_PHRASE,
  isZeroPriceAmount,
  isZeroPriceMaterialLine,
  isZeroForfaitDiagnosticCost,
  getZeroPriceMaterials,
  hasZeroPriceMaterials,
  isDiagnosisMaterialLine,
  isZeroPriceDiagnosisLine,
  getZeroPriceDiagnosisIssues,
  getZeroPricePublishIssues,
  hasZeroPricePublishIssues,
  assertZeroPricesAcknowledged,
  assertZeroMaterialsAcknowledged,
};
